import React, { useState, useEffect } from 'react';
import { Modal, Table, Empty, Typography, Row, Col, Card, Space, Spin } from 'antd';
import { departmentService } from '../../services/departmentService';
import { useTranslation } from 'react-i18next';
import ViewModeSelect from '../common/ViewModeSelect';
import useResponsiveTableView from '../../hooks/useResponsiveTableView';

const { Text } = Typography;

// Two narrow columns (name, position); below this the modal body shows cards instead.
const MIN_TABLE_WIDTH = 420;

const DepartmentMembersModal = ({ open, departmentId, departmentName, onClose }) => {
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [manualView, setManualView] = useState('auto');
  const { containerRef, view } = useResponsiveTableView({ minTableWidth: MIN_TABLE_WIDTH, manualView });

  useEffect(() => {
    if (!open || departmentId == null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    departmentService.getMembers(departmentId)
      .then((data) => {
        if (!cancelled) setMembers(data);
      })
      .catch(() => {
        if (!cancelled) setError(t('department.membersModal.fetchError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, departmentId, t]);

  const columns = [
    {
      title: t('department.membersModal.name'),
      key: 'name',
      render: (_, record) => (
        <Text>{`${record.firstName} ${record.lastName}`}</Text>
      ),
    },
    {
      title: t('department.membersModal.position'),
      dataIndex: 'position',
      key: 'position',
    },
  ];

  const renderCards = () => {
    if (!loading && members.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('department.membersModal.empty')}
        />
      );
    }
    return (
      <Row gutter={[12, 12]}>
        {members.map((member) => (
          <Col xs={24} sm={12} key={member.id}>
            <Card size="small" data-testid="member-card">
              <Space direction="vertical" size={2}>
                <Text strong>{`${member.firstName} ${member.lastName}`}</Text>
                <Text type="secondary">{member.position}</Text>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <Modal
      title={departmentName
        ? `${t('department.membersModal.title')} — ${departmentName}`
        : t('department.membersModal.title')}
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(560px, 95vw)"
      data-testid="department-members-modal"
    >
      {error ? (
        <Text type="danger">{error}</Text>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <ViewModeSelect value={manualView} onChange={setManualView} data-testid="view-mode-select" />
          </div>
          <div ref={containerRef}>
            {view === 'table' ? (
              <Table
                dataSource={members}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={false}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={t('department.membersModal.empty')}
                    />
                  ),
                }}
              />
            ) : (
              <Spin spinning={loading}>{renderCards()}</Spin>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

export default DepartmentMembersModal;
